import type { AIReviewBatchItem, AIReviewBatchItemResult, AIReviewBatchResponse } from 'api';
import type { ReviewItem } from 'store/useReviewStore';
import { reviewItemId } from './reviewAggregator';

/**
 * Group the selected review cards into the per-security batch items the
 * `apiCapTablesAiReviewBatchPartialUpdate` endpoint expects:
 * `[{ security_id, field_names: [...] }]`. Cards are keyed `${securityId}:${fieldAlias}`,
 * so two selected fields on the same security collapse into one item with two
 * field names. Only cards present in `items` (i.e. still in the panel) are
 * included; stale ids in the selection set are ignored.
 */
export const buildBulkItems = (selectedIds: Set<string>, items: ReviewItem[]): AIReviewBatchItem[] => {
  const byId = new Map(items.map(i => [i.id, i]));
  const fieldsBySecurity = new Map<number, string[]>();
  selectedIds.forEach(id => {
    const item = byId.get(id);
    if (!item) {
      return;
    }
    const fields = fieldsBySecurity.get(item.securityId);
    if (fields) {
      fields.push(item.fieldAlias);
    } else {
      fieldsBySecurity.set(item.securityId, [item.fieldAlias]);
    }
  });
  return Array.from(fieldsBySecurity, ([security_id, field_names]) => ({ security_id, field_names }));
};

/**
 * Accept-time client gate: split the selected cards into the batch-eligible ones
 * and the error messages for the rest (`errorFor` returns a message when a card's
 * suggestion cannot be applied on the grid - e.g. an underlying-security NAME that
 * matches no option). Excluded cards must never reach the server (they would
 * settle there while the grid discards the value); their errors are reported
 * through the same partial-failure summary as backend per-item errors, and the
 * cards stay pending.
 */
export const partitionAcceptableItems = (
  items: ReviewItem[],
  errorFor: (item: ReviewItem) => string | undefined
): { acceptable: ReviewItem[]; errors: string[] } => {
  const errors: string[] = [];
  const acceptable = items.filter(item => {
    const error = errorFor(item);
    if (error) {
      errors.push(error);
    }
    return !error;
  });
  return { acceptable, errors };
};

export interface BulkResultSummary {
  /** security_id -> the fields the backend actually settled (for the optimistic patch). */
  settledBySecurity: Map<number, string[]>;
  /** Card ids (`${securityId}:${fieldAlias}`) that moved, in result order. */
  settledIds: string[];
  /** Flattened per-item error messages; non-empty drives the warning snackbar. */
  errorMessages: string[];
}

/**
 * Fold the per-security batch results into the three things the drawer needs: the
 * settled fields per security (for the in-place patch), the flat list of moved card
 * ids (so the panel moves only those cards), and any per-item errors. The batch
 * endpoint always returns 200 with a SUBSET of settled_fields plus per-item errors,
 * so partial success is the normal case: a security that fully failed contributes no
 * settled entry but its errors are still surfaced.
 */
export const summarizeBulkResults = (results: AIReviewBatchItemResult[]): BulkResultSummary => {
  const settledBySecurity = new Map<number, string[]>();
  const settledIds: string[] = [];
  const errorMessages: string[] = [];
  results.forEach(r => {
    if (r.settled_fields.length > 0) {
      settledBySecurity.set(r.security_id, r.settled_fields);
      r.settled_fields.forEach(f => settledIds.push(reviewItemId(r.security_id, f)));
    }
    errorMessages.push(...r.errors);
  });
  return { settledBySecurity, settledIds, errorMessages };
};

/**
 * The AI-CREATED securities a bulk DECLINE deleted server-side (the batch
 * dismissed their LAST remaining finding). Drives the drawer's column removal:
 * server-reported ids replace the old settled-name heuristic, which removed a
 * column the moment its name card was declined even while sibling findings
 * were still pending.
 */
export const deletedSecurityIdsOf = (response: AIReviewBatchResponse | undefined): number[] =>
  response?.deleted_security_ids ?? [];
